import type { RestaurantConfiguration } from '@/types/restaurant';
import type { PublicDeliveryConfig } from './deliveryApi';

/**
 * SC-554 — le seuil applicable au mode de vente en cours, en centimes.
 *
 * Il y a DEUX seuils indépendants (`min_order_amount_emporter` et
 * `min_order_amount_livraison`) et le serveur choisit le sien sur le type de la
 * commande (`sale_api/order/order/views_online.py`, 409 `order_below_minimum`).
 * Le client doit choisir de la même façon, sinon il annonce un seuil que le
 * serveur n'appliquera pas — ou se tait sur celui qu'il applique.
 *
 * Ce sélecteur est partagé par le tiroir du panier et la page de paiement :
 * les deux le lisaient chacun de leur côté, et la page de paiement ne regardait
 * que la livraison. Un panier constitué en « à emporter » puis basculé en
 * « Livraison » — ou l'inverse — sortait alors sans aucun contrôle jusqu'au 409
 * final, après que le client eut rempli adresse, nom, téléphone et e-mail.
 *
 * `dine_in` n'est jamais concerné : le serveur ne lit aucun de ces deux champs
 * pour une commande à table.
 *
 * 0 = aucune restriction.
 */
export function minOrderCentsForMode(
  config: RestaurantConfiguration | null | undefined,
  mode: 'emporter' | 'livraison' | 'dine_in',
): number {
  if (mode === 'dine_in') return 0;
  const cents =
    mode === 'livraison'
      ? config?.minOrderAmountLivraisonCents
      : config?.minOrderAmountEmporterCents;
  return cents ?? 0;
}

/** « 50.00 » -> 5000 ; vide, absent ou illisible -> `undefined` (= pas de surcharge). */
function ttcToCents(value: string | null | undefined): number | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  const n = parseFloat(value);
  return Number.isFinite(n) ? Math.round(n * 100) : undefined;
}

/**
 * SC-1155 — le seuil de commande tenant compte de la ZONE de livraison.
 *
 * Un palier (mode distance) ou une ville (mode ville) peut porter son propre
 * minimum ; vide, c'est le minimum général de la livraison, une valeur — `0`
 * compris — le remplace. Le serveur contrôle sur la zone de l'adresse
 * (409 `order_below_minimum`), le client doit annoncer le même nombre.
 *
 * - Livraison, devis reçu (`quote` non nul) : le minimum du devis s'il en porte
 *   un, sinon le général.
 * - Livraison, pas de devis (tiroir du panier, page de paiement avant
 *   l'adresse) : le PLUS PETIT seuil possible parmi les zones du mode actif —
 *   on ne bloque jamais un panier qu'une zone accepterait. Une zone sans
 *   minimum compte pour le général. Aucune zone / config inconnue : le général.
 * - Emporter et sur place : inchangés (`minOrderCentsForMode`).
 *
 * Le minimum d'un devis est ignoré quand la config de livraison a bien été
 * reçue SANS `zone_mode` : c'est un sale_api d'avant SC-1155, qui glisse dans
 * CHAQUE devis le `min_order_ttc` déprécié de SC-749 (« 0.00 » presque
 * partout) sans le contrôler. Le prendre pour une surcharge de zone éteindrait
 * le garde du minimum général, et le client ne découvrirait le refus qu'au 409
 * — les deux dépôts ne sont pas toujours déployés dans le même geste.
 * Une config ABSENTE (requête en échec, pas encore arrivée) n'est pas un
 * ancien serveur : on se fie alors au devis, seul à connaître la zone.
 *
 * 0 = aucune restriction.
 */
export function minOrderCentsForOrder(
  config: RestaurantConfiguration | null | undefined,
  mode: 'emporter' | 'livraison' | 'dine_in',
  deliveryConfig: PublicDeliveryConfig | null | undefined,
  quote?: { min_order_ttc?: string | null } | null,
): number {
  const general = minOrderCentsForMode(config, mode);
  if (mode !== 'livraison') return general;
  const legacyBackend = deliveryConfig != null && deliveryConfig.zone_mode === undefined;
  if (quote) return (legacyBackend ? undefined : ttcToCents(quote.min_order_ttc)) ?? general;
  const zones = deliveryConfig?.zone_mode === 'CITY' ? deliveryConfig.cities : deliveryConfig?.tiers;
  if (!zones || zones.length === 0) return general;
  return Math.min(...zones.map((z) => ttcToCents(z.min_order_ttc) ?? general));
}
